import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useSearchParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import withRouter from '../../../hoc/with-router';
import { Paths } from '../../../shared/paths';
import { useStyles } from './ViewExercise.styles';
import ViewExerciseSkeleton from './ViewExerciseSkeleton';
import ExerciseHistory from './exercise-history/ExerciseHistory';
import ExerciseStatistics from './exercise-statistics/ExerciseStatistics';
import ExerciseSummary from './exercise-summary/ExerciseSummary';
import { useViewExercisePage } from './useViewExercisePage';

function ViewExercise(props) {
  const { t } = useTranslation();
  const exerciseId = props.params.exerciseId;
  const {
    exercise,
    trainings,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useViewExercisePage(exerciseId);
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const accessQuerySuffix = useMemo(() => {
    const s = searchParams.toString();
    return s ? `?${s}` : '';
  }, [searchParams]);

  const { classes } = useStyles(theme);
  const hasExerciseId = Boolean(exerciseId);
  const isInFlight = hasExerciseId && (isLoading || isFetching);
  const shouldShowLoading = !exercise && isInFlight;
  const shouldShowContent = !!exercise && !shouldShowLoading;
  const handleRetry = React.useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !exercise && isInFlight,
    hasData: Boolean(exercise),
    onRetry: handleRetry,
  });

  const tabsConfig = useMemo(
    () => [
      {
        label: t('exercises.summary'),
        path: `${Paths.exercises.toSummaryId(exerciseId)}${accessQuerySuffix}`,
      },
      {
        label: t('exercises.history'),
        path: `${Paths.exercises.toHistoryId(exerciseId)}${accessQuerySuffix}`,
      },
      {
        label: t('exercises.statistics'),
        path: `${Paths.exercises.toStatisticId(exerciseId)}${accessQuerySuffix}`,
      },
    ],
    [exerciseId, t, accessQuerySuffix],
  );

  return (
    <PageContainer backButton title={t('common.exercise')} onRefresh={refetch}>
      <AppTabs config={tabsConfig} />

      <Box marginTop={1} className={classes.exerciseViewContent}>
        {shouldShowLoading ? <ViewExerciseSkeleton /> : null}

        {shouldShowContent ? (
          <Routes>
            <Route
              path={Paths.exercises.summary}
              element={<ExerciseSummary exercise={exercise} />}
            />
            <Route
              path={Paths.exercises.history}
              element={
                <ExerciseHistory trainings={trainings} exercise={exercise} />
              }
            />
            <Route
              path={Paths.exercises.statistic}
              element={
                <ExerciseStatistics exercise={exercise} trainings={trainings} />
              }
            />
            <Route
              path={'*'}
              element={
                <AppNavigate
                  to={`${Paths.exercises.toSummaryId(exerciseId)}${accessQuerySuffix}`}
                  replace
                />
              }
            />
          </Routes>
        ) : null}
      </Box>
    </PageContainer>
  );
}

export default withRouter(ViewExercise);
